import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { gsap } from '../lib/gsapSetup';
import SectionTag from '../components/SectionTag';
import { CTA } from '../content/siteContent';
import { scrollToId } from '../lib/scrollTo';
import { useReducedMotion } from '../hooks/useMotionPrefs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MagneticButton({ children, onClick, testId, primary }) {
  const btnRef = useRef(null);

  const onMove = (e) => {
    const el = btnRef.current;
    if (!el || !window.matchMedia('(pointer: fine)').matches) return;
    const rect = el.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    gsap.to(el, { x: dx * 0.22, y: dy * 0.28, duration: 0.4, ease: 'power2.out' });
  };
  const onLeave = () => {
    gsap.to(btnRef.current, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
  };

  return (
    <button
      ref={btnRef}
      data-testid={testId}
      data-cursor="OPEN"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      className={`group inline-flex items-center gap-3 px-8 py-5 text-[11px] font-medium uppercase tracking-[0.25em] transition-colors duration-400 ${
        primary
          ? 'bg-[#47a0d9] text-black hover:bg-white'
          : 'border border-white/30 text-white hover:border-[#47a0d9] hover:text-[#47a0d9]'
      }`}
    >
      {children}
      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
    </button>
  );
}

export default function Contact() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduced = useReducedMotion();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || reduced) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.02 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [reduced]);

  useEffect(() => {
    if (reduced) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.cta-line',
        { yPercent: 120 },
        {
          yPercent: 0,
          stagger: 0.12,
          duration: 1,
          ease: 'power4.out',
          scrollTrigger: { trigger: ref.current, start: 'top 65%' },
        }
      );
      gsap.fromTo(
        '.cta-actions',
        { opacity: 0, y: 30, scale: 0.97 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: { trigger: ref.current, start: 'top 50%' },
        }
      );
      gsap.to('.cta-video', {
        yPercent: 10,
        ease: 'none',
        scrollTrigger: { trigger: ref.current, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    }, ref);
    return () => ctx.revert();
  }, [reduced]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success('Enquiry received — the Amaya Energy team will be in touch.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full border-b border-white/20 bg-transparent py-4 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#47a0d9]';

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#000000]">
      {/* Final CTA — cinematic conclusion */}
      <div ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden">
        <video
          ref={videoRef}
          className="cta-video absolute inset-0 h-[115%] w-full object-cover opacity-40 will-change-transform"
          src="/videos/cta.mp4"
          poster="/posters/cta.jpg"
          autoPlay={!reduced}
          muted
          loop
          playsInline
          preload="metadata"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/30 to-[#050505]" />

        <div className="relative z-10 px-5 py-32 md:px-12">
          <SectionTag number="12" label="Partner With Amaya" />
          <h2
            className="mt-10 font-display font-bold leading-[0.95] tracking-tight text-white"
            style={{ fontSize: 'clamp(2rem, 7vw, 6.5rem)' }}
          >
            {CTA.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <span className="cta-line block will-change-transform">
                  {line.replace('.', '')}
                  {line.endsWith('.') && <span className="text-[#47a0d9]">.</span>}
                </span>
              </span>
            ))}
          </h2>
          <div className="cta-actions mt-12 flex flex-col gap-4 sm:flex-row">
            <MagneticButton testId="cta-partner-btn" primary onClick={() => scrollToId('contact-form')}>
              {CTA.primary}
            </MagneticButton>
          </div>
        </div>
      </div>

      {/* Contact form */}
      <div id="contact-form" className="relative border-t border-white/10 px-5 py-24 md:px-12 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-16 md:grid-cols-2">
          <div>
            <h3 className="font-display text-2xl font-semibold tracking-tight text-white md:text-4xl">
              Start a conversation
            </h3>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
              Partnerships, brand ventures and ecosystem enquiries — tell us where you want energy to move next.
            </p>
          </div>

          {sent ? (
            <div data-testid="contact-success" className="flex flex-col items-start justify-center gap-4 border border-[#47a0d9]/40 bg-[#47a0d9]/5 p-10">
              <span className="h-2 w-2 rounded-full bg-[#47a0d9]" />
              <p className="font-display text-2xl font-semibold uppercase tracking-tight text-white">
                Enquiry received
              </p>
              <p className="text-sm text-white/60">
                Thank you — your message has been saved and the team will respond shortly.
              </p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-6">
              <input
                data-testid="contact-name-input"
                required
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-company-input"
                placeholder="Company (optional)"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className={inputCls}
              />
              <textarea
                data-testid="contact-message-input"
                required
                rows={4}
                placeholder="Message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="group mt-2 inline-flex w-fit items-center gap-3 bg-[#47a0d9] px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white disabled:opacity-60"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : null}
                Send enquiry
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 px-5 py-10 md:px-12">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <span className="flex items-center gap-2.5">
            <img src="/logo-icon.png" alt="Amaya Energy logo" className="h-6 w-6" />
            <span className="font-display text-sm font-bold tracking-[0.12em] text-white">Amaya Energy</span>
          </span>
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
            © 2026 Amaya Energy — Energy in Motion
          </p>
        </div>
      </footer>
    </section>
  );
}
